import { memo, useState } from "react"

import { cn } from "@productlane/lib/src/browser"
import { Tooltip } from "@productlane/ui"

import type { OutcomeCounts } from "./agent-outcomes-model"
import type { AgentBucket } from "@/components/reporting/agent-buckets"
import { agentBucketColumns } from "@/components/reporting/agent-buckets"
import { REPORTING_X_AXIS_HEIGHT } from "@/components/reporting/charts"
import { api } from "@/lib/api"
import { outcomeShares } from "./agent-outcomes-model"
import {
	FORECAST_WINDOW_DAYS,
	forecastRange,
	formatShare,
} from "./resolution-forecast-model"

// A handoff is a chance for the next suggestion, not a failure, so it stays green.
const SWATCH: Record<AgentBucket, string> = {
	resolved: "bg-green-500",
	escalated: "bg-green-500/50",
	pending: "bg-gray-300 dark:bg-gray-600",
}

/**
 * The resolution rate over the forecast window. The bar splits the agent's
 * conversations by how they ended; each segment names its count on hover.
 */
export const AgentOutcomesCard = memo(
	({ className }: { className?: string }) => {
		// The same range as the history chart's counts, so the query is shared.
		const [range] = useState(() => forecastRange(new Date()))
		const resolutions =
			api.workspace.getReportingAgentResolutionsByOrigin.useQuery(range, {
				staleTime: 5 * 60_000,
			})

		if (resolutions.isLoading) {
			return (
				<div
					className={cn(
						"h-[164px] animate-pulse rounded-xl bg-white dark:bg-gray-900",
						className,
					)}
				/>
			)
		}

		const counts: OutcomeCounts = {
			resolved: resolutions.data?.resolved ?? 0,
			escalated: resolutions.data?.escalated ?? 0,
			pending: resolutions.data?.pending ?? 0,
		}
		const sessions = resolutions.data?.sessions ?? 0
		const label = (bucket: AgentBucket) =>
			agentBucketColumns.find((column) => column.bucket === bucket)?.label ??
			bucket

		return (
			<section
				data-testid="agent-outcomes"
				className={cn(
					"flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700/80 dark:bg-gray-900",
					className,
				)}
			>
				<div className="flex flex-col gap-0.5">
					<h2 className="text-sm font-medium text-primary">Resolution rate</h2>
					<p className="text-xs text-tertiary">
						{sessions > 0
							? `The agent resolved ${counts.resolved} of ${sessions} conversations in the last ${FORECAST_WINDOW_DAYS} days.`
							: `The agent had no conversations in the last ${FORECAST_WINDOW_DAYS} days.`}
					</p>
				</div>
				{sessions > 0 && (
					<>
						<span
							data-testid="agent-resolution-rate"
							className="text-3xl font-semibold tabular-nums tracking-tight text-primary"
						>
							{formatShare(counts.resolved / sessions)}
						</span>
						<div
							className="mt-auto flex h-2.5 gap-0.5"
							// Sits on the baseline of the chart beside it, above its date labels.
							style={{ marginBottom: REPORTING_X_AXIS_HEIGHT }}
						>
							{outcomeShares(counts).map(({ bucket, share }) => (
								<Tooltip
									key={bucket}
									label={`${counts[bucket]} ${label(bucket).toLowerCase()}`}
									side="top"
								>
									<div
										data-testid={`agent-outcome-${bucket}`}
										className={cn("h-full rounded-[1px]", SWATCH[bucket])}
										style={{ flexGrow: share }}
									/>
								</Tooltip>
							))}
						</div>
					</>
				)}
			</section>
		)
	},
)
